import { FC, useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  createColumnHelper,
  ColumnDef,
  Row,
  SortingState,
} from '@tanstack/react-table';
import { Tag, InlineLoading } from '@carbon/react';
import { Pagination } from 'components/pagination';
import { PageHeader } from 'components/page-header';
import { StatsBar } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import {
  subtractGeometriesLoadableAtom,
  tractGeometriesLoadableAtom,
  tractGroupFilterAtom,
} from './atoms';
import type {
  DuplicateGeometryRecord,
  SubtractGroup,
  TractGroup,
  GeometryStats,
} from './types';
import styles from './page.module.scss';

const recordColumnHelper = createColumnHelper<DuplicateGeometryRecord>();

const makeRecordColumns = (
  idField: 'subtractId' | 'tractId',
  nameField: 'subtractName' | 'tractName'
) => [
  recordColumnHelper.accessor('id', {
    id: 'id',
    header: 'ID',
    size: 100,
    meta: { type: 'integer' },
  }),
  recordColumnHelper.accessor('uuid', {
    id: 'uuid',
    header: 'UUID',
    size: 300,
    cell: (info) => info.getValue() ?? '--',
  }),
  recordColumnHelper.accessor(idField, {
    id: idField,
    header: idField === 'subtractId' ? 'Subtract ID' : 'Tract ID',
    size: 220,
  }),
  recordColumnHelper.accessor(nameField, {
    id: nameField,
    header: nameField === 'subtractName' ? 'Subtract Name' : 'Tract Name',
    size: 180,
    cell: (info) => info.getValue() ?? '--',
  }),
  recordColumnHelper.accessor('source', {
    id: 'source',
    header: 'Source',
    size: 100,
  }),
  recordColumnHelper.accessor('keep', {
    id: 'keep',
    header: 'Action',
    size: 100,
    cell: (info) => {
      const keep = info.getValue();
      return (
        <Tag type={keep ? 'green' : 'red'} size="sm">
          {keep ? 'Keep' : 'Remove'}
        </Tag>
      );
    },
  }),
  recordColumnHelper.accessor('updatedAt', {
    id: 'updatedAt',
    header: 'Updated At',
    size: 240,
    meta: { type: 'datetime' },
  }),
];

const subtractRecordColumns = makeRecordColumns('subtractId', 'subtractName');
const tractRecordColumns = makeRecordColumns('tractId', 'tractName');

// --- Subtract group table ---

const subtractGroupHelper = createColumnHelper<SubtractGroup>();

const subtractGroupColumns = [
  subtractGroupHelper.accessor('subtractId', {
    id: 'subtractId',
    header: 'Subtract ID',
    size: 220,
  }),
  subtractGroupHelper.accessor('subtractName', {
    id: 'subtractName',
    header: 'Subtract Name',
    size: 200,
    cell: (info) => info.getValue() ?? '--',
  }),
  subtractGroupHelper.accessor((row) => row.records.length, {
    id: 'recordCount',
    header: 'Record Count',
    size: 140,
    meta: { type: 'integer' },
  }),
  subtractGroupHelper.accessor(
    (row) => row.records.filter((r) => !r.keep).length,
    {
      id: 'toRemove',
      header: 'To Remove',
      size: 120,
      meta: { type: 'integer' },
    }
  ),
  subtractGroupHelper.accessor(
    (row) =>
      row.records
        .flatMap((r) => [String(r.id), r.uuid, r.source])
        .filter(Boolean)
        .join(' '),
    {
      id: '_nestedSearch',
      header: '',
      size: 0,
      enableSorting: false,
    }
  ),
];

// --- Tract group table ---

const tractGroupHelper = createColumnHelper<TractGroup>();

const tractGroupColumns = [
  tractGroupHelper.accessor('tractId', {
    id: 'tractId',
    header: 'Tract ID',
    size: 220,
  }),
  tractGroupHelper.accessor('tractName', {
    id: 'tractName',
    header: 'Tract Name',
    size: 200,
    cell: (info) => info.getValue() ?? '--',
  }),
  tractGroupHelper.accessor('recordCount', {
    id: 'recordCount',
    header: 'Record Count',
    size: 140,
    meta: { type: 'integer' },
  }),
  tractGroupHelper.accessor((row) => row.recordCount - 1, {
    id: 'toRemove',
    header: 'To Remove',
    size: 120,
    meta: { type: 'integer' },
  }),
  tractGroupHelper.accessor(
    (row) =>
      row.records
        .flatMap((r) => [String(r.id), r.uuid, r.source])
        .filter(Boolean)
        .join(' '),
    {
      id: '_nestedSearch',
      header: '',
      size: 0,
      enableSorting: false,
    }
  ),
];

const RecordSubComponent: FC<{
  records: DuplicateGeometryRecord[];
  columns: ColumnDef<DuplicateGeometryRecord>[];
  persistenceKey: string;
}> = ({ records, columns, persistenceKey }) => (
  <div className={styles.groupContent}>
    <DataTable
      data={records}
      columns={columns}
      getRowId={(row) => String(row.id)}
      persistenceKey={persistenceKey}
      config={{
        sorting: false,
        globalSearch: false,
        columnFilters: false,
        columnResizing: false,
        columnReordering: false,
        columnPinning: false,
        columnVisibility: false,
        fitContent: true,
      }}
    />
  </div>
);

const StatsSection: FC<{ stats: GeometryStats; label: string }> = ({
  stats,
  label,
}) => (
  <StatsBar
    stats={[
      {
        label: `${label} Records`,
        value: stats.totalRecords,
        tagType: 'gray',
      },
      {
        label: 'Duplicate Groups',
        value: stats.duplicateGroups,
        tagType: 'red',
      },
      {
        label: 'Records to Keep',
        value: stats.recordsToKeep,
        tagType: 'green',
      },
      {
        label: 'Records to Remove',
        value: stats.recordsToRemove,
        tagType: 'red',
      },
    ]}
  />
);

export const DuplicateGeometriesPage: FC = () => {
  const subtractLoadable = useAtomValue(subtractGeometriesLoadableAtom);
  const tractLoadable = useAtomValue(tractGeometriesLoadableAtom);
  const [tractFilters, setTractFilters] = useAtom(tractGroupFilterAtom);

  const handleTractSortChange = useCallback(
    (sorting: SortingState) => {
      setTractFilters((prev) => ({
        ...prev,
        sortColumn: sorting[0]?.id ?? null,
        sortDirection: sorting[0]?.desc ? 'desc' : 'asc',
        page: 1,
      }));
    },
    [setTractFilters]
  );

  const subtractGroups =
    subtractLoadable.state === 'hasData'
      ? (subtractLoadable.data?.groups ?? [])
      : [];
  const subtractStats =
    subtractLoadable.state === 'hasData'
      ? (subtractLoadable.data?.stats ?? null)
      : null;
  const subtractLoading = subtractLoadable.state === 'loading';
  const subtractError =
    subtractLoadable.state === 'hasError'
      ? subtractLoadable.error instanceof Error
        ? subtractLoadable.error
        : new Error('Failed to fetch subtract data')
      : null;

  const tractGroups =
    tractLoadable.state === 'hasData' ? (tractLoadable.data?.groups ?? []) : [];
  const tractStats =
    tractLoadable.state === 'hasData'
      ? (tractLoadable.data?.stats ?? null)
      : null;
  const tractLoading = tractLoadable.state === 'loading';
  const tractError =
    tractLoadable.state === 'hasError'
      ? tractLoadable.error instanceof Error
        ? tractLoadable.error
        : new Error('Failed to fetch tract data')
      : null;

  return (
    <>
      <PageHeader
        breadcrumbs={[]}
        title="Duplicate Geometries"
        actions={null}
      />

      <div className={styles.sectionHeader}>
        <div className={styles.sectionDescription}>
          Subtract and tract geometry records that share the same subtract_id or
          tract_id but have different UUIDs. This happens because ArcGIS assigns
          new UUIDs on re-export and the Kafka ingestors use UUID-based lookups.
          <br />
          <br />
          For each group, the newest record (by updated_at) is marked
          &quot;Keep&quot; and older records are marked &quot;Remove&quot;.
        </div>
        <div className={styles.expectedState}>
          Expected: Empty - no duplicate geometries should exist
        </div>
      </div>

      {/* Subtract Geometries Section */}
      <div className={styles.geometryTypeSection}>
        <h3 className={styles.geometryTypeTitle}>Subtract Geometries</h3>

        {subtractLoading && (
          <InlineLoading description="Loading subtract duplicates..." />
        )}

        {subtractError && (
          <div className={styles.errorMessage}>
            Error loading subtract data: {subtractError.message}
          </div>
        )}

        {!subtractLoading && !subtractError && (
          <>
            {subtractStats && (
              <StatsSection stats={subtractStats} label="Subtract" />
            )}

            <div className={styles.tableWrapper}>
              <DataTable
                data={subtractGroups}
                columns={subtractGroupColumns as ColumnDef<SubtractGroup>[]}
                getRowId={(row) => row.subtractId}
                persistenceKey="duplicate-subtract-geometries"
                initialTableState={{
                  columnVisibility: { _nestedSearch: false },
                }}
                renderRowSubComponent={(row: Row<SubtractGroup>) => (
                  <RecordSubComponent
                    records={row.original.records}
                    columns={
                      subtractRecordColumns as ColumnDef<DuplicateGeometryRecord>[]
                    }
                    persistenceKey={`dup-subtract-records-${row.original.subtractId}`}
                  />
                )}
                config={{
                  sorting: true,
                  globalSearch: true,
                  columnFilters: true,
                  columnResizing: false,
                  columnReordering: true,
                  columnPinning: false,
                  columnVisibility: false,
                  fitContent: true,
                }}
              />
            </div>
          </>
        )}
      </div>

      {/* Tract Geometries Section */}
      <div className={styles.geometryTypeSection}>
        <h3 className={styles.geometryTypeTitle}>Tract Geometries</h3>

        {tractLoading && (
          <InlineLoading description="Loading tract duplicates..." />
        )}

        {tractError && (
          <div className={styles.errorMessage}>
            Error loading tract data: {tractError.message}
          </div>
        )}

        {!tractLoading && !tractError && (
          <>
            {tractStats && <StatsSection stats={tractStats} label="Tract" />}

            <div className={styles.tableWrapper}>
              <DataTable
                data={tractGroups}
                columns={tractGroupColumns as ColumnDef<TractGroup>[]}
                getRowId={(row) => row.tractId}
                persistenceKey="duplicate-tract-geometries"
                initialTableState={{
                  columnVisibility: { _nestedSearch: false },
                }}
                onSortChange={(s) => handleTractSortChange(s.sorting)}
                renderRowSubComponent={(row: Row<TractGroup>) => (
                  <RecordSubComponent
                    records={row.original.records}
                    columns={
                      tractRecordColumns as ColumnDef<DuplicateGeometryRecord>[]
                    }
                    persistenceKey={`dup-tract-records-${row.original.tractId}`}
                  />
                )}
                config={{
                  sorting: true,
                  globalSearch: true,
                  columnFilters: true,
                  columnResizing: false,
                  columnReordering: true,
                  columnPinning: false,
                  columnVisibility: false,
                  fitContent: true,
                }}
              />

              <Pagination
                page={tractFilters.page}
                pageSize={tractFilters.perPage}
                pageSizes={[25, 50, 100]}
                totalItems={
                  tractLoadable.state === 'hasData'
                    ? (tractLoadable.data?.pagination.totalCount ?? 0)
                    : 0
                }
                onChange={({
                  page,
                  pageSize,
                }: {
                  page: number;
                  pageSize: number;
                }) => {
                  setTractFilters((prev) => ({
                    ...prev,
                    page,
                    perPage: pageSize,
                  }));
                }}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};
